import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { DirectoryEntry, listUserDirectory, saveRaceRules } from '../auth/api';
import { useTimingSocket } from '../useTimingSocket';
import { useStrategyPlan } from '../hooks/useStrategyPlan';
import {
  CALENDAR_2026,
  MAX_PIT_MARGIN_MINUTES,
  PIT_MARGIN_MINUTES,
  RACE_CATEGORIES,
  RACE_DURATIONS,
  RaceCategory,
  RaceDuration,
  StintBias,
  TRACKS,
  TRACK_IDS,
  TrackId,
  directionChangeMinute,
} from '../analytics/regulations';
import {
  clockAt,
  formatMinutes,
  formatMinutesSeconds,
  formatOffset,
} from '../analytics/stintPlan';
import { RaceRules } from '../types';
import { navigate } from './EventsList';
import { SessionBadge } from './SessionBadge';
import StintGrid from './StintGrid';
import './StrategyPage.css';

/** The regulation bounds, mapped onto the shape the live screens already read. */
function toRaceRules(summary: ReturnType<typeof useStrategyPlan>['summary']): RaceRules {
  const c = summary.constraints;
  return {
    maxStintMinutes: c.maxStintMinutes,
    // Left at the default: how early to warn is a preference, not a regulation.
    dueSoonMinutes: 10,
    requiredStops: c.requiredStops,
    minDriverMinutes: c.minDriverMinutes,
    maxDriverMinutes: c.maxDriverMinutes,
    minPitSeconds: c.minPitSeconds,
    minStintMinutes: c.minStintMinutes > 0 ? c.minStintMinutes : null,
  };
}

function localDateTimeValue(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(
    at.getHours(),
  )}:${pad(at.getMinutes())}`;
}

/**
 * The pre-race strategy planner, at /strategy.
 *
 * A route rather than a panel because it has to work when nothing is running:
 * an event row only exists once a race is being recorded, and this is the sheet
 * a team fills in the week before. It reads the timing socket only to offer the
 * two hand-offs at the bottom — pushing the derived rules and the roster onto
 * the race once one is live.
 */
export const StrategyPage: React.FC = () => {
  const s = useStrategyPlan();
  const { isAdmin, isSuperAdmin, user } = useAuth();
  const { activeEvent, teams } = useTimingSocket();
  const [newDriver, setNewDriver] = useState('');
  const [handoff, setHandoff] = useState<string | null>(null);
  const [directory, setDirectory] = useState<DirectoryEntry[]>([]);

  // Everyone a plan could be shared with. A failure only costs the picker.
  useEffect(() => {
    let cancelled = false;
    listUserDirectory()
      .then((rows) => {
        if (!cancelled) setDirectory(rows);
      })
      .catch(() => {
        if (!cancelled) setDirectory([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { plan, summary, violations } = s;
  const c = summary.constraints;

  const planLevel = useMemo(
    () => violations.filter((v) => v.stintIndex === null),
    [violations],
  );
  const errorCount = violations.filter((v) => v.severity === 'error').length;

  /**
   * Which race the hand-offs act on: whatever the plan is attached to, else the
   * one recording now. An explicit attachment wins, so a sheet filed against a
   * past race cannot push its rules at today's session by accident.
   */
  const target = useMemo<{ id: string; title: string } | null>(() => {
    const live = activeEvent ? { id: activeEvent.id, title: activeEvent.title } : null;
    if (!plan.eventId) return live;
    const found = s.events.find((ev) => ev.id === plan.eventId);
    if (found) return { id: found.id, title: found.title };
    // Attached to the race recording now, before the list has caught up with it.
    return live?.id === plan.eventId ? live : null;
  }, [plan.eventId, s.events, activeEvent]);

  const applyRules = useCallback(async () => {
    if (!target) return;
    setHandoff(null);
    try {
      await saveRaceRules(target.id, toRaceRules(summary));
      setHandoff(`Race rules applied to “${target.title}”.`);
    } catch (err) {
      setHandoff(err instanceof Error ? err.message : 'Could not apply the rules');
    }
  }, [target, summary]);

  /** The recording race's teams, when this plan is attached to that race. */
  const liveTeams = useMemo(
    () =>
      activeEvent && plan.eventId === activeEvent.id
        ? Array.from(teams.values()).sort(
            (a, b) => (parseInt(a.kartNumber, 10) || 0) - (parseInt(b.kartNumber, 10) || 0),
          )
        : [],
    [activeEvent, plan.eventId, teams],
  );

  /** Only the owner (or the super admin) changes who else can open the plan. */
  const canShare = !plan.id || plan.ownerId === user?.id || isSuperAdmin;
  const shareable = directory.filter((d) => d.id !== (plan.ownerId ?? user?.id));
  const sharedIds = new Set(plan.sharedWith.map((a) => a.id));
  const toggleShare = (entry: DirectoryEntry): void => {
    s.setSharedWith(
      sharedIds.has(entry.id)
        ? plan.sharedWith.filter((a) => a.id !== entry.id)
        : [...plan.sharedWith, entry],
    );
  };

  const recordedStints = Object.keys(plan.actuals).length;

  const availableCount = plan.roster.filter((d) => !d.unavailable).length;

  // `deriveConstraints` clamps an out-of-range squad into the legal band, so an
  // empty roster reports the 2-driver minimum. Pushing that onto a live race
  // would tell the compliance panel every driver owes ten hours.
  const squadIsLegal =
    availableCount >= c.minDrivers && availableCount <= c.maxDrivers;

  const directionChangeAt = directionChangeMinute(
    plan.config.durationHours,
    plan.config.directionChange,
  );

  return (
    <div className="App strategy-page">
      <div className="app-header">
        <h1>Team Strategy</h1>
        <div className="sp-header-actions">
          {s.dirty && <span className="sp-dirty">Unsaved changes</span>}
          <button type="button" className="header-toggle" onClick={() => navigate('/pit')}>
            Pit board
          </button>
          <button type="button" className="header-toggle" onClick={() => navigate('/')}>
            ← Live timing
          </button>
          <SessionBadge />
        </div>
      </div>

      {s.error && (
        <div className="sp-error" role="alert">
          {s.error}
          <button type="button" onClick={s.dismissError} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      {s.conflict && (
        <div className="sp-error" role="alert">
          <span>
            {s.conflict.ownerName ?? 'Someone'} saved a newer version at{' '}
            {new Date(s.conflict.updatedAt).toLocaleTimeString()}.
          </span>
          <button type="button" onClick={s.acceptTheirs}>
            Load their version
          </button>
          <button type="button" onClick={() => void s.keepMine()}>
            Keep mine and save
          </button>
        </div>
      )}

      <div className="sp-body">
        <div className="sp-side">
          <section className="sp-card">
            <h2>Race</h2>
            <label className="sp-field">
              <span>Title</span>
              <input
                type="text"
                value={plan.title}
                onChange={(e) => s.setTitle(e.target.value)}
                aria-label="Strategy title"
              />
            </label>

            <label className="sp-field">
              <span>Round</span>
              <select
                aria-label="Round from the 2026 calendar"
                value=""
                onChange={(e) => {
                  const round = CALENDAR_2026[Number(e.target.value)];
                  if (!round) return;
                  s.setConfig({
                    trackId: round.trackId,
                    durationHours: round.durations[0],
                  });
                }}
              >
                <option value="">Pick from the 2026 calendar…</option>
                {CALENDAR_2026.map((r, i) => (
                  <option key={i} value={i}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="sp-row">
              <label className="sp-field">
                <span>Length</span>
                <select
                  aria-label="Race length"
                  value={plan.config.durationHours}
                  onChange={(e) =>
                    s.setConfig({ durationHours: Number(e.target.value) as RaceDuration })
                  }
                >
                  {RACE_DURATIONS.map((d) => (
                    <option key={d} value={d}>
                      {d} hours
                    </option>
                  ))}
                </select>
              </label>

              <label className="sp-field">
                <span>Category</span>
                <select
                  aria-label="Category"
                  value={plan.config.category}
                  onChange={(e) =>
                    s.setConfig({ category: e.target.value as RaceCategory })
                  }
                >
                  {RACE_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="sp-field">
              <span>Track</span>
              <select
                aria-label="Track"
                value={plan.config.trackId}
                onChange={(e) => s.setConfig({ trackId: e.target.value as TrackId })}
              >
                {TRACK_IDS.map((id) => (
                  <option key={id} value={id}>
                    {TRACKS[id].name} — {TRACKS[id].minPitSeconds}s stop
                  </option>
                ))}
              </select>
            </label>

            <label className="sp-field">
              <span>Starts at</span>
              <input
                type="datetime-local"
                aria-label="Race start"
                value={localDateTimeValue(plan.config.startsAt)}
                onChange={(e) => {
                  const at = new Date(e.target.value);
                  if (!Number.isNaN(at.getTime())) {
                    s.setConfig({ startsAt: at.toISOString() });
                  }
                }}
              />
            </label>

            <label className="sp-check">
              <input
                type="checkbox"
                aria-label="Direction changes at the midpoint"
                checked={plan.config.directionChange}
                onChange={(e) => s.setConfig({ directionChange: e.target.checked })}
              />
              <span>
                Direction changes at the midpoint
                <small>Uncheck if the bulletin declares one direction (rain).</small>
              </span>
            </label>

            <fieldset className="sp-profile">
              <legend>Stint profile</legend>
              <p className="sp-note">
                Rule 10 fixes the number of stops, so this only moves driving
                time between stints — it cannot buy you fewer of them.
              </p>

              <label className="sp-field">
                <span>Weighting</span>
                <select
                  aria-label="Stint weighting"
                  value={plan.config.stintBias}
                  onChange={(e) =>
                    s.setConfig({ stintBias: e.target.value as StintBias })
                  }
                >
                  <option value="even">Equal stints</option>
                  <option value="front">Longer at the start</option>
                  <option value="back">Longer at the end</option>
                </select>
              </label>

              <label className="sp-check">
                <input
                  type="checkbox"
                  aria-label="Use the maximum stint time"
                  checked={plan.config.maximiseStintTime}
                  disabled={plan.config.stintBias === 'even'}
                  onChange={(e) =>
                    s.setConfig({ maximiseStintTime: e.target.checked })
                  }
                />
                <span>
                  Use the maximum stint time
                  <small>
                    {plan.config.stintBias === 'even'
                      ? 'Pick a weighting first — with equal stints there is no end to run long.'
                      : `Run at ${c.maxStintMinutes} min for as many stints as the rules allow; the rest drop toward ${c.planningFloorMinutes} min.`}
                  </small>
                </span>
              </label>

              <label className="sp-check">
                <input
                  type="checkbox"
                  aria-label="Double-stint where allowed"
                  checked={plan.config.preferDoubleStints}
                  disabled={c.mustChangeDriverEveryStop}
                  onChange={(e) =>
                    s.setConfig({ preferDoubleStints: e.target.checked })
                  }
                />
                <span>
                  Double-stint where allowed
                  <small>
                    {c.mustChangeDriverEveryStop
                      ? 'SILVER must change driver at every pit entry (rule 11b).'
                      : 'Keep a driver in for two stints, changing only the kart between them (rule 11c).'}
                  </small>
                </span>
              </label>
              <label className="sp-field">
                <span>Clear of a closed pit lane</span>
                <input
                  type="number"
                  min={0}
                  max={MAX_PIT_MARGIN_MINUTES}
                  aria-label="Minutes clear of a closed pit lane"
                  value={plan.config.pitMarginMinutes}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (Number.isInteger(n) && n >= 0 && n <= MAX_PIT_MARGIN_MINUTES) {
                      s.setConfig({ pitMarginMinutes: n });
                    }
                  }}
                />
                <small className="sp-note">
                  Minutes kept either side of every closure. A closure that starts
                  early costs 3 laps, and the moment is the marshals&rsquo;, not the
                  clock&rsquo;s. Set 0 to plan right up to the line.
                </small>
              </label>
            </fieldset>

            <label className="sp-field">
              <span>Mandatory stops</span>
              <input
                type="number"
                min={0}
                aria-label="Mandatory stops override"
                placeholder={String(c.requiredStops)}
                value={plan.config.requiredStopsOverride ?? ''}
                onChange={(e) =>
                  s.setConfig({
                    requiredStopsOverride:
                      e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />
              <small className="sp-note">
                Rule 10 is titled “driver changes” but rule 20j penalises “pit stops”.
                For {plan.config.category} they only differ if you take kart-only
                stops — override here if your briefing says otherwise.
              </small>
            </label>
          </section>

          <section className="sp-card">
            <h2>
              Drivers <span className="sp-count">{availableCount} available</span>
            </h2>
            {plan.roster.length === 0 && (
              <p className="sp-muted">
                Add between {c.minDrivers} and {c.maxDrivers} drivers for a{' '}
                {plan.config.durationHours}-hour race.
              </p>
            )}
            <ul className="sp-roster" aria-label="Drivers">
              {plan.roster.map((d, i) => {
                const total = summary.driverTotals[i];
                return (
                  <li key={i} className={d.unavailable ? 'sp-out' : ''}>
                    <span className="sp-driver-name">{d.name}</span>
                    <span className="sp-driver-time">
                      {formatMinutes(total?.minutes ?? 0)}
                      {total?.shortfallMinutes ? (
                        <b className="sp-short">
                          {' '}
                          owes {formatMinutes(total.shortfallMinutes)}
                        </b>
                      ) : null}
                    </span>
                    <span className="sp-driver-actions">
                      <button type="button" onClick={() => s.moveDriver(i, -1)} aria-label={`Move ${d.name} up`}>
                        ↑
                      </button>
                      <button type="button" onClick={() => s.moveDriver(i, 1)} aria-label={`Move ${d.name} down`}>
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => s.toggleAvailable(i)}
                        aria-label={`${d.unavailable ? 'Mark available' : 'Mark unavailable'}: ${d.name}`}
                      >
                        {d.unavailable ? '☐' : '☑'}
                      </button>
                      <button type="button" onClick={() => s.removeDriver(i)} aria-label={`Remove ${d.name}`}>
                        ×
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="sp-add">
              <input
                type="text"
                value={newDriver}
                placeholder="Add driver…"
                aria-label="Add a driver"
                onChange={(e) => setNewDriver(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    s.addDriver(newDriver);
                    setNewDriver('');
                  }
                }}
              />
              <button
                type="button"
                disabled={!newDriver.trim()}
                onClick={() => {
                  s.addDriver(newDriver);
                  setNewDriver('');
                }}
              >
                Add
              </button>
            </div>
          </section>

          <section className="sp-card">
            <h2>Race record</h2>
            <label className="sp-field">
              <span>Attached to</span>
              <select
                aria-label="Attach this strategy to a race"
                value={plan.eventId ?? ''}
                onChange={(e) => s.setEvent(e.target.value || null)}
              >
                <option value="">Not attached — just a plan</option>
                {activeEvent && !s.events.some((ev) => ev.id === activeEvent.id) && (
                  <option value={activeEvent.id}>{activeEvent.title} · recording now</option>
                )}
                {s.events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title} · {ev.trackName} ·{' '}
                    {new Date(ev.startedAt).toLocaleDateString()}
                    {ev.endedAt === null ? ' · recording now' : ''}
                  </option>
                ))}
              </select>
              <small className="sp-note">
                Attaching lines the sheet up with a race — live, so the rules go to
                it and its stint plan is followed on the live screen, or past, so
                the plan is kept with the race it was written for.
              </small>
            </label>

            <label className="sp-field">
              <span>Team</span>
              {liveTeams.length > 0 ? (
                <select
                  aria-label="Team this strategy runs for"
                  value={plan.teamId ?? ''}
                  onChange={(e) => {
                    const team = liveTeams.find((t) => t.id === e.target.value);
                    s.setTeam(team ? team.id : null, team ? team.kartNumber || null : plan.kartNumber);
                  }}
                >
                  <option value="">Pick a team on the grid…</option>
                  {liveTeams.map((t) => (
                    <option key={t.id} value={t.id}>
                      #{t.kartNumber} {t.teamName || t.driver}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  aria-label="Kart number this strategy runs for"
                  placeholder="Kart number"
                  value={plan.kartNumber ?? ''}
                  onChange={(e) => s.setTeam(plan.teamId, e.target.value.trim() || null)}
                />
              )}
              <small className="sp-note">
                {liveTeams.length > 0
                  ? 'The live screen follows this plan for that team. Karts get swapped during a race; the team stays.'
                  : 'Attach to the race while it is recording to pick the team from the grid. Until then the kart number is a hint.'}
              </small>
            </label>
          </section>

          <section className="sp-card">
            <h2>
              Access <span className="sp-count">{plan.sharedWith.length} shared</span>
            </h2>
            <p className="sp-note">
              {plan.ownerName ? `Owned by ${plan.ownerName}. ` : ''}
              Everyone listed can open and edit this plan and follow it live. The
              super admin can always see it.
            </p>
            {plan.sharedWith.length === 0 && shareable.length === 0 && (
              <p className="sp-muted">There is nobody else to share with yet.</p>
            )}
            <ul className="sp-share" aria-label="Accounts this strategy is shared with">
              {(canShare ? shareable : plan.sharedWith).map((entry) => (
                <li key={entry.id}>
                  <label className="sp-check">
                    <input
                      type="checkbox"
                      checked={sharedIds.has(entry.id)}
                      disabled={!canShare}
                      onChange={() => toggleShare(entry as DirectoryEntry)}
                    />
                    <span>{entry.displayName}</span>
                  </label>
                </li>
              ))}
            </ul>
            {!canShare && (
              <p className="sp-note">Only the owner can change who this is shared with.</p>
            )}
          </section>

          {recordedStints > 0 && (
            <section className="sp-card">
              <h2>Recorded from a race</h2>
              <p className="sp-note">
                {recordedStints} stint{recordedStints === 1 ? '' : 's'} of this plan have been
                recorded from the timing feed. Clear them before using the plan in another race.
              </p>
              <button type="button" onClick={() => void s.resetActuals()}>
                Clear recorded stints
              </button>
            </section>
          )}

          <section className="sp-card sp-rules">
            <h2>The rules that follow</h2>
            <dl>
              <div><dt>Mandatory stops</dt><dd>{c.requiredStops}</dd></div>
              <div><dt>Minimum pit stop</dt><dd>{c.minPitSeconds}s</dd></div>
              <div><dt>Stint</dt>
                <dd>
                  {c.minStintMinutes > 0 ? `${c.minStintMinutes}–` : 'up to '}
                  {c.maxStintMinutes} min
                </dd>
              </div>
              <div><dt>First stint</dt><dd>max {c.maxFirstStintMinutes} min</dd></div>
              <div><dt>Per driver</dt>
                <dd>
                  {c.minDriverMinutes ? `min ${formatMinutes(c.minDriverMinutes)}` : 'no minimum'}
                  {c.maxDriverMinutes ? ` · max ${formatMinutes(c.maxDriverMinutes)}` : ''}
                </dd>
              </div>
              <div><dt>Squad</dt><dd>{c.minDrivers}–{c.maxDrivers} drivers</dd></div>
            </dl>
            {directionChangeAt !== null && (
              <div className="sp-reverse-when">
                <span>Direction changes at</span>
                <b>{clockAt(plan.config.startsAt, directionChangeAt)}</b>
                <small>{formatOffset(directionChangeAt)} into the race</small>
              </div>
            )}

            <h3>Pit lane closed</h3>
            <ul className="sp-windows">
              {c.closedPitWindows.map((w, i) => (
                <li key={i}>
                  <b>{formatMinutes(w.fromMinute)}–{formatMinutes(w.toMinute)}</b> {w.reason}
                </li>
              ))}
              <li className="sp-muted">
                Plus whenever a safety kart is out, and for 5 minutes after it returns —
                called on the day, so it cannot be planned around.
              </li>
            </ul>
            <p className="sp-margin-note">
              Stops are planned {PIT_MARGIN_MINUTES} minutes clear either side of
              these. A closure that starts early costs 3 laps, and the exact
              moment is the marshals&rsquo;, not the clock&rsquo;s. Where the margin
              cannot fit, the plan stays legal and flags the tight stop.
            </p>
          </section>
        </div>

        <div className="sp-main">
          <div className="sp-toolbar">
            <button type="button" className="sp-primary" onClick={s.generate}>
              Generate plan
            </button>
            <button type="button" onClick={s.save} disabled={s.saving}>
              {s.saving ? 'Saving…' : plan.id ? 'Save' : 'Save as new'}
            </button>
            <button type="button" onClick={s.startNew}>
              New
            </button>
            <button type="button" onClick={() => window.print()}>
              Print sheet
            </button>
            <select
              aria-label="Open a saved strategy"
              value=""
              onChange={(e) => e.target.value && s.load(e.target.value)}
            >
              <option value="">Open saved…</option>
              {s.saved.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.title} · {row.config.durationHours}h {row.config.category}
                </option>
              ))}
            </select>
            <span className={`sp-verdict ${errorCount === 0 ? 'ok' : 'bad'}`}>
              {plan.stints.length === 0
                ? 'No plan yet'
                : errorCount === 0
                  ? '✓ Legal plan'
                  : `${errorCount} problem${errorCount === 1 ? '' : 's'}`}
            </span>
          </div>

          {planLevel.length > 0 && (
            <ul className="sp-issues">
              {planLevel.map((v, i) => (
                <li key={i} className={`sp-issue--${v.severity}`}>
                  <span className="sp-rule">Rule {v.rule}</span>
                  {v.message}
                  {v.penalty && v.penalty !== '—' && (
                    <span className="sp-penalty">{v.penalty}</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="sp-totals">
            <span><b>{summary.stops}</b> stops</span>
            <span><b>{summary.driverChanges}</b> driver changes</span>
            {summary.kartOnlyStops > 0 && (
              <span><b>{summary.kartOnlyStops}</b> kart-only</span>
            )}
            <span>Driving <b>{formatOffset(summary.totalDrivingMinutes)}</b></span>
            <span>
              In the pits <b>{formatOffset(summary.totalPitMinutes)}</b>
              <small> ({summary.stops}&times;{formatMinutesSeconds(c.minPitMinutes)})</small>
            </span>
            <span>
              Total <b>{formatOffset(summary.plannedMinutes)}</b> of{' '}
              {formatOffset(c.raceMinutes)}
            </span>
          </div>

          <StintGrid
            stints={plan.stints}
            roster={plan.roster}
            config={plan.config}
            violations={violations}
            minPitMinutes={c.minPitMinutes}
            directionChange={
              c.closedPitWindows.find((w) => w.kind === 'midpoint') ?? null
            }
            onChange={s.setStint}
          />

          {target && isAdmin && (
            <section className="sp-card sp-handoff">
              <h2>Apply to “{target.title}”</h2>
              <p className="sp-muted">
                {activeEvent?.id === target.id
                  ? 'Recording now — the live screens run on default rules until these are pushed.'
                  : 'A past race. Applying the rules makes its replay and stats read against the right numbers.'}
              </p>
              {handoff && <div className="sp-handoff-note">{handoff}</div>}
              <div className="sp-handoff-actions">
                <button
                  type="button"
                  onClick={applyRules}
                  disabled={!squadIsLegal}
                  title={
                    squadIsLegal
                      ? undefined
                      : `Set a roster of ${c.minDrivers}–${c.maxDrivers} drivers first — the per-driver minimum depends on it.`
                  }
                >
                  Apply race rules
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default StrategyPage;
